import localeEn from '@fullcalendar/core/locales/en-gb'
import localeFi from '@fullcalendar/core/locales/fi'
import localeNl from '@fullcalendar/core/locales/nl'
import localeSv from '@fullcalendar/core/locales/sv'
import Tooltip from '@mui/material/Tooltip'
import { useQuery } from '@tanstack/react-query'
import { differenceInCalendarDays } from 'date-fns'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { calendarPatch, personsGetActiveQuery } from '../../api'
import { FormInput } from '../../common/form/forminput.jsx'
import ResourceTimelineCalendar from '../../common/fullcalendar/ResourceTimelineCalendar.jsx'
import { PageContent } from '../../common/layout/common.js'
import LoadingCentered from '../../common/loaders/loadingcentered'
import { TimelineEvent } from '../../common/timelinevent/timelineevent.jsx'
import { useCalendarEvents } from '../../hooks/useCalendarEvents.js'
import i18n from '../../i18n'
import { queryClient } from '../../main.jsx'
import { NewAbscenceModal } from '../personalprofile/newabscencemodal/newabscencemodal'
import { CalendarFilters } from './calendarfilters.jsx'
import { CalendarHeaderDay, CalendarHeaderWeek } from './calendarheaders.jsx'
import { CalendarToolbar } from './calendarToolbar.jsx'
import { minusOneDay } from './calendarUtils.js'

const localeMap = {
	fi: localeFi,
	sv: localeSv,
	en: localeEn,
	nl: localeNl,
}

const formDefaults = {
	causeCode: undefined,
	status: undefined,
	search: '',
	supervisor: undefined,
	payGroup: '',
}

const queryKey = 'calendarEvents'

// https://fullcalendar.io/docs/premium
export function CalendarView() {
	const { t } = useTranslation()
	const [personsList, setPersonsList] = useState([])
	const [hasActiveFilters, setHasActiveFilters] = useState(false)
	const [event, setEvent] = useState()
	const [dateRange, setDateRange] = useState(null)
	const [activeEventId, setActiveEventId] = useState(null)
	const [appliedRange, setAppliedRange] = useState(null)
	// ISO string range of what the calendar is currently displaying — safe to use during render
	const [calendarViewRange, setCalendarViewRange] = useState({
		start: null,
		end: null,
	})
	// True once FullCalendar has mounted — avoids reading calendarRef.current during render
	const [calendarInstance, setCalendarInstance] = useState(null)

	// Keeps handleDatesSet up to date without stale closure
	const appliedRangeRef = useRef(null)

	useEffect(() => {
		appliedRangeRef.current = appliedRange
	}, [appliedRange])

	// Set to true before any programmatic gotoDate so handleDatesSet skips the sync
	const skipDatesSetSyncRef = useRef(false)

	const { data: personas } = useQuery(personsGetActiveQuery())

	const persons = personas?.data
		? personas?.data.map((person) => ({
				id: person.personId,
				title: person.name,
				nr: person.nr,
				...person,
			}))
		: personas?.data

	const calendarRef = useRef(null)

	const today = DateTime.now()

	// Stabilise calendarDuration: only create a new object when the day count actually changes
	const calendarDayCount = appliedRange ? differenceInCalendarDays(appliedRange.end, appliedRange.start) + 1 : null
	const calendarDuration = useMemo(
		() => (calendarDayCount !== null ? { days: calendarDayCount } : { weeks: 8 }),
		[calendarDayCount],
	)

	const handleDatesSet = ({ startStr, endStr }) => {
		const start = startStr.slice(0, 10)
		const end = minusOneDay(endStr.slice(0, 10))
		setDateRange({ start, end })
		setCalendarViewRange({ start, end })
		// Sync applied range with the new visible window when the user navigates
		// (skip when the change was triggered by a programmatic gotoDate call)
		if (skipDatesSetSyncRef.current) {
			skipDatesSetSyncRef.current = false
			return
		}
		if (appliedRangeRef.current) {
			// Guard: only update if the dates actually changed, to avoid unnecessary re-renders
			const prevStart = appliedRangeRef.current.start?.toISOString().slice(0, 10)
			const prevEnd = appliedRangeRef.current.end?.toISOString().slice(0, 10)
			if (start !== prevStart || end !== prevEnd) {
				setAppliedRange({
					start: new Date(`${start}T00:00:00`),
					end: new Date(`${end}T23:59:59`),
				})
			}
		}
	}

	const handleRangeApply = (start, end) => {
		const calendarApi = calendarRef.current.getApi()
		skipDatesSetSyncRef.current = true
		calendarApi.gotoDate(DateTime.fromJSDate(start).toISODate())
		setAppliedRange({ start, end })
	}

	const handleRangeReset = () => {
		const calendarApi = calendarRef.current.getApi()
		const start = DateTime.now().startOf('week')
		const end = start.plus({ weeks: 8 }).minus({ days: 1 })
		skipDatesSetSyncRef.current = true
		calendarApi.gotoDate(start.toISODate())
		setAppliedRange({ start: start.toJSDate(), end: end.toJSDate() })
	}

	// No filters: use full persons list. Filters active: use personsList (can be empty)
	const resetPersonsList = () => {
		setPersonsList([])
		setHasActiveFilters(false)
	}

	const activePersonsList = hasActiveFilters ? personsList : persons || []

	const form = useForm({
		defaultValues: formDefaults,
		mode: 'onChange',
	})

	const {
		allEvents: events,
		absenceEvents,
		holidayDates,
		isLoading,
		error,
	} = useCalendarEvents({
		queryKey,
		dateRange,
		enabled: !!dateRange,
		t,
		language: i18n.resolvedLanguage,
	})

	/**
	 * @param {import("@fullcalendar/interaction").DateClickArg} info
	 */
	const handleDateClick = (info) => {
		setActiveEventId(null)
		const calendarApi = calendarRef.current.getApi()
		setEvent({
			start: info.dateStr,
			end: info.dateStr,

			personId: info.resource?.id,
			personNr: info.resource?.extendedProps?.nr,
			personName: info.resource?.title,

			calendarApi,
		})
	}

	const handleDateRangeSelect = (info) => {
		setActiveEventId(null)
		if (!info.resource) return // Skip if no resource (i.e., clicked in empty space)
		const calendarApi = calendarRef.current.getApi()
		setEvent({
			start: info.startStr,
			end: minusOneDay(info.endStr),
			personId: info.resource.id,
			personNr: info.resource.extendedProps.nr,
			personName: info.resource.title,

			calendarApi,
		})
	}

	/**
	 * @param {import("@fullcalendar/core").EventClickArg} info
	 */
	const handleEventClick = ({ event, jsEvent }) => {
		jsEvent.preventDefault()
		jsEvent.stopPropagation()
		setActiveEventId((prev) => {
			const newActiveId = prev === event.id ? null : event.id
			return newActiveId
		})
	}

	const openEditModalFromEvent = (fcEvent) => {
		const extended = fcEvent.extendedProps || {}
		const resources = fcEvent.getResources()
		const resource = resources?.[0]

		const currentUser = resource?.extendedProps // Pass the resource (the personJSON attached to the event) as currentUser

		const modalEvent = {
			id: fcEvent.id,
			start: fcEvent.startStr,
			end: minusOneDay(fcEvent.endStr),
			title: fcEvent.title,
			info: fcEvent.title,
			allDay: fcEvent.allDay,
			personId: resource?.id || fcEvent.extendedProps.personId,
			personNr: resource?.extendedProps?.nr || fcEvent.extendedProps.personNr,
			personName: resource?.title || fcEvent.extendedProps.personName,
			causeCode: extended.causeCode,
			icdCode: extended.icdCode,
			status: extended.status,
			dayHours: extended.dayHours,
			longAbsence: extended.longAbsence,
			ownAnnouncement: extended.ownAnnouncement,
			approvers: extended.approvers || [],
			calendarApi: calendarRef.current?.getApi(),
			extendedProps: { ...extended },
			currentUser,
		}

		setEvent(modalEvent)
	}

	const handleEventDropResize = async (info) => {
		const { event } = info

		try {
			const resourceId = event.getResources()?.[0]?.id
			await calendarPatch(event.id, {
				personId: Number(resourceId),
				startDate: event.startStr,
				endDate: minusOneDay(event.endStr),
				causeCode: event.extendedProps.causeCode,
				icdCode: event.extendedProps.icdCode,
				status: event.extendedProps.status,
				info: event.title,
			})
			queryClient.invalidateQueries({ queryKey: [queryKey] }) // refetch data
		} catch (error) {
			console.error('Failed to update event', error)
			info.revert() // revert the change if API call fails
		}
	}

	const submitForm = async (formValues) => {
		const filters = {
			causeCode: formValues?.causeCode?.value || undefined,
			status: formValues?.status?.value,
			search: formValues?.search,
			supervisor: formValues?.supervisor?.value,
			payGroup: formValues?.payGroup,
		}

		const filteredEvents = events
			.filter((event) => {
				if (!filters.causeCode) {
					return event
				}
				return event.causeCode === filters.causeCode
			})
			.filter((event) => {
				if (!filters.status) {
					return event
				}
				return event.status === filters.status
			})
			.map((event) => event.resourceId)
			.filter((event) => !!event)

		const searchLower = filters.search?.toLowerCase() || ''

		const newPersonsList = (persons || []).filter((person) => {
			if (!searchLower) return true

			const nameMatch = person.title.toLowerCase().includes(searchLower)
			const nrMatch = person.nr?.toString().includes(searchLower)

			return nameMatch || nrMatch
		})

		if (!filters.search && !filters.causeCode && !filters.status && !filters.supervisor && !filters.payGroup) {
			resetPersonsList()
			return
		}

		setHasActiveFilters(true)
		setPersonsList(
			newPersonsList
				.filter((person) => {
					if (filters.causeCode || filters.status) {
						return filteredEvents.includes(person.id)
					}
					return person
				})
				.filter((person) => {
					if (!filters.supervisor) {
						return person
					}
					const selectedSupervisorNumber = filters.supervisor.toString()
					const supervisor1Number = person.controlDetails?.supervisor?.personNumber?.toString()
					const supervisor2Number = person.controlDetails?.supervisor2?.personNumber?.toString()

					return supervisor1Number === selectedSupervisorNumber || supervisor2Number === selectedSupervisorNumber
				})
				.filter((person) => {
					if (!filters.payGroup) {
						return person
					}
					return person.controlDetails?.payGroup?.toLowerCase().includes(filters.payGroup.toLowerCase())
				}),
		)
	}

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (!e.target.closest('.fc-event, .timeline-event-popover')) {
				setActiveEventId(null)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	return (
		<FormProvider {...form}>
			<div style={{ display: 'contents' }}>
				{isLoading && <LoadingCentered show />}
				{error && (
					<Alert variant="danger" className="text-center">
						{error.message}
					</Alert>
				)}
				{event && <NewAbscenceModal event={event} setIsOpen={setEvent} />}
				<PageContent>
					{calendarInstance && (
						<CalendarToolbar
							calendarRef={calendarInstance}
							appliedRange={appliedRange}
							viewRange={dateRange}
							onApply={handleRangeApply}
							onReset={handleRangeReset}
							dateRange
						/>
					)}
					<CalendarFilters
						events={events}
						personsList={persons || []}
						setPersonsList={setPersonsList}
						resetPersonsList={resetPersonsList}
						submitForm={submitForm}
					/>
					<ResourceTimelineCalendar
						calendarRef={calendarRef}
						onCalendarMount={setCalendarInstance}
						duration={calendarDuration}
						locale={localeMap[i18n.resolvedLanguage]}
						editable
						selectable
						dateClick={handleDateClick}
						eventClick={handleEventClick}
						eventDrop={handleEventDropResize}
						eventResize={handleEventDropResize}
						select={handleDateRangeSelect}
						eventContent={(event) => {
							const eventData = absenceEvents?.find(({ id }) => id.toString() === event.event.id)
							if (!eventData) return null
							const isActive = activeEventId?.toString() === eventData.id.toString()
							return (
								<TimelineEvent
									event={eventData}
									isActive={isActive}
									onEdit={() => {
										const fcEvent = calendarRef.current.getApi().getEventById(eventData.id)
										setActiveEventId(null)
										openEditModalFromEvent(fcEvent)
									}}
									onClose={() => setActiveEventId(null)}
									calendarViewStartDate={calendarViewRange.start}
									calendarViewEndDate={calendarViewRange.end}
									isTeam
								/>
							)
						}}
						slotLaneContent={(arg) => {
							const dateStr = DateTime.fromJSDate(arg.date).toFormat('yyyy-MM-dd')
							const holidayName = holidayDates.get(dateStr)
							if (!holidayName) return null
							return (
								<Tooltip title={holidayName} placement="top" followCursor enterDelay={500} leaveDelay={200}>
									<span style={{ position: 'absolute', inset: 0 }} />
								</Tooltip>
							)
						}}
						slotLaneClassNames={(arg) => {
							const dateStr = DateTime.fromJSDate(arg.date).toFormat('yyyy-MM-dd')
							return holidayDates.has(dateStr) ? ['fc-holiday'] : []
						}}
						resources={
							activePersonsList.length
								? activePersonsList
								: [
										{
											id: '1',
											title: t('No employees match the selected filters.'),
											nr: '1',
										},
									]
						}
						resourceAreaHeaderContent={() => (
							<FormInput name="search" inputProps={{ placeholder: t('Search', 'Haku') }} label={t('Search', 'Haku')} />
						)}
						resourceLabelContent={(resource) => {
							return (
								<div title={`${resource.resource.title} (${resource.resource.extendedProps.nr})`}>
									{resource.resource.title}
								</div>
							)
						}}
						events={absenceEvents}
						datesSet={handleDatesSet}
						slotLabelContent={(cell) => {
							const cellDate = DateTime.fromJSDate(cell.date)
							const rangeStartDt = appliedRange ? DateTime.fromJSDate(appliedRange.start) : null
							const rangeEndDt = appliedRange ? DateTime.fromJSDate(appliedRange.end) : null

							if (cell.level === 0) {
								const isCurrentWeek = cellDate.weekNumber === today.weekNumber && cellDate.year === today.year
								const month = cellDate.plus({ days: 3 }).setLocale(i18n.resolvedLanguage).monthLong
								const viewStart = dateRange ? DateTime.fromISO(dateRange.start) : null
								const isFocused =
									!isCurrentWeek &&
									viewStart !== null &&
									cellDate.weekNumber === viewStart.weekNumber &&
									cellDate.year === viewStart.year
								const isWeekInRange =
									!isCurrentWeek &&
									!isFocused &&
									rangeStartDt !== null &&
									cellDate.startOf('day') <= rangeEndDt &&
									cellDate.plus({ days: 6 }).endOf('day') >= rangeStartDt
								return (
									<CalendarHeaderWeek
										isCurrent={isCurrentWeek}
										isFocused={isFocused}
										isInRange={isWeekInRange}
										month={month}
										year={`${cellDate.year}–${String(cellDate.weekNumber).padStart(2, '0')}`}
									/>
								)
							}

							if (cell.level === 1) {
								const dateStr = cellDate.toFormat('yyyy-MM-dd')
								const holidayName = holidayDates.get(dateStr)
								return (
									<CalendarHeaderDay
										isCurrent={cellDate.hasSame(today, 'day')}
										day={holidayName ? `${cellDate.day}.${cellDate.month}` : cellDate.day}
										isHoliday={!!holidayName}
										holidayName={holidayName}
									/>
								)
							}
						}}
						eventDataTransform={(event) => ({
							...event,
							end: DateTime.fromISO(event.end).plus({ days: 1 }).toFormat('yyyy-MM-dd'),
						})}
					/>
				</PageContent>
			</div>
		</FormProvider>
	)
}
